import React, { useState } from 'react';
import { 
  Box, Paper, Typography, Grid, TextField, Button, Divider, 
  FormControlLabel, Switch, Tabs, Tab, Card, CardContent, 
  List, ListItem, ListItemText, IconButton
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const Settings = () => {
  const [tabValue, setTabValue] = useState(0);
  const [retentionPeriod, setRetentionPeriod] = useState(30);
  const [enableEncryption, setEnableEncryption] = useState(true);
  const [enableOptInStorage, setEnableOptInStorage] = useState(true);
  const [apiKey, setApiKey] = useState('sk_test_123456789012345678901234');
  
  // Sample data for API endpoints
  const apiEndpoints = [
    { id: 1, name: 'Primary DNS Log Ingestion', url: 'https://api.shadowai.com/v1/ingest', status: 'Active' },
    { id: 2, name: 'Backup DNS Log Ingestion', url: 'https://api-backup.shadowai.com/v1/ingest', status: 'Standby' },
  ];
  
  // Sample data for connected systems
  const connectedSystems = [
    { id: 1, name: 'Acme Corp DNS Servers', type: 'Direct Connection', status: 'Connected' },
    { id: 2, name: 'Network Monitoring System', type: 'SIEM Integration', status: 'Connected' },
  ];
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle toggle changes
  const handleEncryptionToggle = () => {
    setEnableEncryption(!enableEncryption);
  };
  
  const handleOptInStorageToggle = () => {
    setEnableOptInStorage(!enableOptInStorage);
  };
  
  // Handle retention period change
  const handleRetentionPeriodChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 0) {
      setRetentionPeriod(value);
    }
  };
  
  // Handle API key change and reset
  const handleApiKeyChange = (e) => {
    setApiKey(e.target.value);
  };
  
  const handleResetApiKey = () => {
    // In a real application, this would generate a new API key
    setApiKey('sk_test_' + Math.random().toString(36).substring(2, 15));
  };
  
  // Handle form submissions
  const handleSaveGeneralSettings = () => {
    alert('General settings saved successfully!');
  };
  
  const handleSaveDataSettings = () => {
    alert('Data settings saved successfully!');
  };
  
  const handleSaveAPISettings = () => {
    alert('API settings saved successfully!');
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      <Paper sx={{ bgcolor: 'background.paper', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<SettingsIcon />} label="General" id="tab-0" />
          <Tab icon={<StorageIcon />} label="Data & Storage" id="tab-1" />
          <Tab icon={<SecurityIcon />} label="API & Integration" id="tab-2" />
        </Tabs>
        
        {/* General Settings Tab */}
        {tabValue === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              General Settings
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  variant="outlined"
                  defaultValue="Acme Corp"
                  margin="normal"
                />
                
                <TextField
                  fullWidth
                  label="Admin Email"
                  variant="outlined"
                  defaultValue="admin@example.com"
                  margin="normal"
                />
                
                <TextField
                  fullWidth
                  label="Time Zone"
                  variant="outlined"
                  defaultValue="America/New_York"
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      UI Settings
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={true}
                          color="primary"
                        />
                      }
                      label="Dark Mode"
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={true}
                          color="primary"
                        />
                      }
                      label="Show AI Risk Indicators"
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={false}
                          color="primary"
                        />
                      }
                      label="Real-time Updates"
                    />
                  </CardContent>
                </Card>
                
                <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Notification Preferences
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={true}
                          color="primary"
                        />
                      }
                      label="Email Alerts"
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={false}
                          color="primary"
                        />
                      }
                      label="Slack Notifications"
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={false}
                          color="primary"
                        />
                      }
                      label="Mobile Push Notifications"
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<SaveIcon />}
                onClick={handleSaveGeneralSettings}
              >
                Save Settings
              </Button>
            </Box>
          </Box>
        )}
        
        {/* Data & Storage Tab */}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Data & Storage Settings
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  DNS Log Retention
                </Typography>
                
                <TextField
                  fullWidth
                  label="Retention Period (days)"
                  type="number"
                  variant="outlined"
                  value={retentionPeriod}
                  onChange={handleRetentionPeriodChange}
                  margin="normal"
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <InfoIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Setting this to 0 will disable log retention entirely.
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="subtitle1" gutterBottom>
                  Security Options
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={enableEncryption}
                      onChange={handleEncryptionToggle}
                      color="primary"
                    />
                  }
                  label="Enable Data Encryption"
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 2 }}>
                  Encrypt all stored DNS logs at rest.
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={enableOptInStorage}
                      onChange={handleOptInStorageToggle}
                      color="primary"
                    />
                  }
                  label="Enable Opt-In DNS Log Storage"
                />
                
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                  Store DNS logs for retrospective analysis when new AI domains are detected.
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ mb: 2, bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Storage Usage
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">DNS Logs</Typography>
                      <Typography variant="body2">1.2 GB</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Analytics Data</Typography>
                      <Typography variant="body2">0.5 GB</Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">System Data</Typography>
                      <Typography variant="body2">0.1 GB</Typography>
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Total</Typography>
                      <Typography variant="subtitle2">1.8 GB</Typography>
                    </Box>
                  </CardContent>
                </Card>
                
                <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Data Management
                    </Typography>
                    
                    <Button 
                      variant="outlined" 
                      color="error" 
                      startIcon={<DeleteIcon />}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Clear All DNS Logs
                    </Button>
                    
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      startIcon={<StorageIcon />}
                      fullWidth
                    >
                      Export All Data
                    </Button>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <InfoIcon color="warning" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        Clearing logs cannot be undone. Make sure to export your data first.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<SaveIcon />}
                onClick={handleSaveDataSettings}
              >
                Save Settings
              </Button>
            </Box>
          </Box>
        )}
        
        {/* API & Integration Tab */}
        {tabValue === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              API & Integration Settings
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  API Access
                </Typography>
                
                <TextField
                  fullWidth
                  label="API Key"
                  variant="outlined"
                  value={apiKey}
                  onChange={handleApiKeyChange}
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                  }}
                />
                
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={handleResetApiKey}
                  >
                    Reset API Key
                  </Button>
                </Box>
                
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 3 }}>
                  <InfoIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Resetting your API key will invalidate all existing integrations.
                  </Typography>
                </Box>
                
                <Typography variant="subtitle1" gutterBottom>
                  API Endpoints
                </Typography>
                
                <List>
                  {apiEndpoints.map((endpoint) => (
                    <ListItem key={endpoint.id} sx={{ px: 0 }}>
                      <ListItemText
                        primary={endpoint.name}
                        secondary={
                          <>
                            <Typography variant="body2" component="span" color="text.secondary">
                              URL: {endpoint.url}
                            </Typography>
                            <br />
                            <Typography variant="body2" component="span" color="text.secondary">
                              Status: {endpoint.status}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Connected Systems
                </Typography>
                
                <List>
                  {connectedSystems.map((system) => (
                    <Card key={system.id} variant="outlined" sx={{ mb: 2, bgcolor: 'background.default' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography variant="subtitle1">
                            {system.name}
                          </Typography>
                          <IconButton size="small" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary">
                          Type: {system.type}
                        </Typography>
                        
                        <Typography variant="body2" color="text.secondary">
                          Status: {system.status}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <Button size="small" variant="outlined">
                            View Details
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    startIcon={<AddIcon />}
                    fullWidth
                  >
                    Add New System
                  </Button>
                </List>
                
                <Divider sx={{ my: 3 }} />
                
                <Typography variant="subtitle1" gutterBottom>
                  Integration Options
                </Typography>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={true}
                      color="primary"
                    />
                  }
                  label="Enable SIEM Integration"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={false}
                      color="primary"
                    />
                  }
                  label="Enable SSO Authentication"
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={true}
                      color="primary"
                    />
                  }
                  label="Auto-update AI Domain Database"
                />
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<SaveIcon />}
                onClick={handleSaveAPISettings}
              >
                Save Settings
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Settings;